'use client'

import Link from 'next/link'
import { SmartHeader } from '@/widgets/smart-header'
import { Footer } from '@/widgets/footer'
import { CellForm } from '@/widgets/cell-form'
import { motion } from 'framer-motion'
import { MapPin, Zap, Shield, TrendingUp, FileCheck, Award } from 'lucide-react'

// Metadata moved to layout or separate metadata file

export default function JuridicalAddressMoscowPage() {
  return (
    <div className="relative flex min-h-screen flex-col">
      <SmartHeader />
      <main className="flex-1 pt-14">
        {/* Hero секция */}
        <section className="container pt-12 md:pt-20 pb-6 md:pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-3xl md:text-5xl font-bold">Юридические адреса в Москве</h1>
            <p className="mb-8 text-xl text-muted-foreground">
              Московские адреса для регистрации ООО и ИП. Быстрое оформление, гарантированное
              прохождение регистрации в ИФНС.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>От 25000 ₽/мес</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>Оформление за 1 день</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>100% прохождение в ИФНС</span>
              </div>
            </div>
          </div>
        </section>

        {/* Форма заявки */}
        <section className="container py-3 bg-muted/20">
          <div className="mx-auto max-w-2xl">
            <CellForm />
          </div>
        </section>

        {/* Преимущества московских адресов */}
        <section className="container py-6 md:py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6"
            >
              Преимущества московского юридического адреса
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Престиж столицы</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Московский адрес повышает доверие клиентов, банков и партнеров по всей России.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Банковские преимущества</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Лучшие условия открытия счетов, быстрое одобрение кредитов и овердрафтов.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Доступ к тендерам</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Участие в московских госзакупках, федеральных тендерах, корпоративных закупках.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Быстрая регистрация</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Московские ИФНС работают оперативно, регистрация ООО за 1-3 дня.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Лицензирование</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Московские лицензии действуют по всей РФ, упрощенная процедура получения.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Деловая среда</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Networking, доступ к инвесторам, участие в деловых мероприятиях столицы.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Тарифы */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Тарифы на юридические адреса в Москве
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-8 text-center">
                <h3 className="text-xl font-semibold mb-4">Стандартный</h3>
                <div className="text-3xl font-bold text-orange-600 mb-4">25 000 ₽/мес</div>
                <ul className="text-sm space-y-3 text-left mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Московский район</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Гарантия прохождения ИФНС</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Прием корреспонденции</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Уведомления о почте</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Все документы</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground">Для ООО и ИП, базовый пакет услуг</p>
              </div>

              <div className="bg-white rounded-lg p-8 text-center border-2 border-orange-200">
                <div className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded mb-4 inline-block">
                  Популярный
                </div>
                <h3 className="text-xl font-semibold mb-4">Бизнес</h3>
                <div className="text-3xl font-bold text-orange-600 mb-4">35 000 ₽/мес</div>
                <ul className="text-sm space-y-3 text-left mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Центральный район Москвы</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Все из тарифа &quot;Стандартный&quot;</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Курьерская доставка почты</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Скан документов на email</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Переадресация звонков</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground">
                  Для серьезного бизнеса, расширенный пакет услуг
                </p>
              </div>

              <div className="bg-white rounded-lg p-8 text-center">
                <h3 className="text-xl font-semibold mb-4">Премиум</h3>
                <div className="text-3xl font-bold text-orange-600 mb-4">45 000 ₽/мес</div>
                <ul className="text-sm space-y-3 text-left mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Центральный район Москвы</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Все из тарифа &quot;Бизнес&quot;</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Представительский офис</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Переговорные комнаты</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Персональный менеджер</span>
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground">
                  Для крупного бизнеса, полный пакет услуг
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Процесс оформления */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Как оформить юридический адрес в Москве
            </h2>

            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Заявка и выбор адреса</h3>
                  <p className="text-muted-foreground">
                    Оставьте заявку с указанием района Москвы. Предложим несколько вариантов адресов
                    с разной ценовой категорией.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Подготовка документов</h3>
                  <p className="text-muted-foreground">
                    Подготовим все необходимые документы: гарантийные письма, согласия собственника,
                    выписки из ЕГРН.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Заключение договора</h3>
                  <p className="text-muted-foreground">
                    Подписываем договор аренды юридического адреса. Возможно оформление в офисе или
                    курьерской доставкой.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Готов к использованию</h3>
                  <p className="text-muted-foreground">
                    Адрес готов для подачи документов в ИФНС. Гарантируем успешную регистрацию ООО
                    или ИП.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Альтернатива - готовые ООО */}
        <section className="container py-12 bg-gradient-to-r from-orange-50 to-orange-100">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-8 text-2xl md:text-3xl font-bold">
              💡 Альтернатива: Готовое ООО с московским адресом
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Зачем тратить время на регистрацию нового ООО, когда можно купить готовое с московским
              адресом?
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold text-orange-600 mb-3">🚀 Готовое ООО</h3>
                <ul className="text-sm space-y-2 text-left">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Готов к работе сразу</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Московский юр. адрес включен</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>Банковские счета открыты</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span>
                    <span>История деятельности</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold text-red-600 mb-3">⏰ Новая регистрация</h3>
                <ul className="text-sm space-y-2 text-left">
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Время на регистрацию 1-3 дня</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Аренда адреса отдельно</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Ожидание открытия счетов</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span>
                    <span>Нет истории операций</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/ru/kupit-ooo-moskva"
                className="inline-flex items-center justify-center px-8 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Посмотреть готовые ООО в Москве
              </Link>
              <Link
                href="/ru/prodat-ooo-moskva"
                className="inline-flex items-center justify-center px-8 py-3 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Продать свое ООО
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Часто задаваемые вопросы
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-3">
                  Можно ли использовать адрес для регистрации ООО и ИП одновременно?
                </h3>
                <p className="text-muted-foreground">
                  Да, один московский адрес можно использовать для регистрации множества юридических
                  лиц и ИП. Ограничений по количеству нет.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-3">
                  Что входит в стоимость аренды юридического адреса?
                </h3>
                <p className="text-muted-foreground">
                  В стоимость входит: гарантийное письмо, согласие собственника, прием
                  корреспонденции, уведомления о почте, все документы для ИФНС.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-3">
                  Можно ли сменить юридический адрес в будущем?
                </h3>
                <p className="text-muted-foreground">
                  Да, адрес можно сменить в любой момент. Поможем с подготовкой документов для смены
                  адреса в ИФНС и уведомления контролирующих органов.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
